import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

const HeroSection = ({ userData }) => {
  const [currentTime, setCurrentTime] = useState(new Date())
  const [terminalLines, setTerminalLines] = useState([])
  const [showInterface, setShowInterface] = useState(false)
  const [currentCommand, setCurrentCommand] = useState('')
  const [commandHistory, setCommandHistory] = useState([])
  const [historyIndex, setHistoryIndex] = useState(-1)
  const [isInputFocused, setIsInputFocused] = useState(false)
  const [isTerminalExpanded, setIsTerminalExpanded] = useState(false)
  const [terminalHeight, setTerminalHeight] = useState(320) // 20rem = 320px
  const [isResizing, setIsResizing] = useState(false)
  const [resizeStart, setResizeStart] = useState({ y: 0, startHeight: 0 })
  const inputRef = useRef(null)
  const terminalRef = useRef(null)

  // Update time every second for live clock
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000)
    return () => clearInterval(timer)
  }, [])

  // Simulate terminal startup sequence
  useEffect(() => {
    const startupSequence = [
      "$ whoami",
      userData?.personal?.name || "QuantDoppler"
    ]

    let index = 0
    const interval = setInterval(() => {
      if (index < startupSequence.length) {
        setTerminalLines(prev => [...prev, startupSequence[index]])
        index++
      } else {
        clearInterval(interval)
        setTimeout(() => setShowInterface(true), 1000)
      }
    }, 400)

    return () => clearInterval(interval)
  }, [userData])

  // Command processing function
  const processCommand = (cmd) => {
    const command = cmd.toLowerCase().trim()
    const responses = {
      '/help': [
        'Available commands:',
        '  /help     - Show this help menu',
        '  /about    - Bureau manifest — who we are',
        '  /skills   - View technical capabilities',
        '  /projects - Deployed programs and missions',
        '  /roadmap  - View mission timeline',
        '  /contact  - Initiate secure uplink',
        '  /joke     - Classified humor (use with caution)',
        '  /clear    - Clear terminal (also: clear, /cls, cls)',
        '  /time     - Show current time',
        '  /status   - Mission status report'
      ],
      '/about': [
        'QuantDoppler — Sovereign Systems Engineer',
        'AI-Driven Architect. Deep-Tech Builder.',
        '',
        'A deep-tech engineering bureau forging enterprise-grade',
        'systems for industries that cannot fail.',
        '',
        'From first telemetry to live deployment — single trajectory. 🛰️'
      ],
      '/skills': [
        'Technical Capabilities:',
        '• Languages: Python, JavaScript, C++, TypeScript, C#',
        '• Frameworks: React, Node.js, Flask, .NET',
        '• Databases: PostgreSQL, MongoDB, MySQL, SQLite',
        '• Cloud & CI/CD: AWS, Docker, CI/CD Pipelines',
        '• AI/ML: TensorFlow, LSTM, NLP, Deep Learning',
        '',
        'Doctrine: engineer it once. deploy it right. 🔒'
      ],
      '/projects': [
        'Deployed Programs:',
        '• Hackronomics — AI-powered financial intelligence system',
        '• Milestone — Full-stack nonprofit operations platform',
        '• Yelp Analytics — ETL + ML-driven data intelligence',
        '',
        'Scroll down to review the full mission logs. 🛰️'
      ],
      '/roadmap': [
        'Mission Timeline Initialized 🌌',
        '',
        'Descending into mission history...',
        'Trajectory log loading...',
        '',
        'From launchpad to command center — trajectory unlocked. 🚀'
      ],
      '/contact': [
        'Secure Uplink Channels:',
        '• GitHub: https://github.com/quantdoppler20',
        '• Email: Mission-critical inquiries only',
        '',
        'Response window: < 24 hours. Zero handoffs. 📡'
      ],
      '/joke': [
        'Why do engineers prefer dark mode?',
        '',
        'Because light attracts bugs — and we have zero-tolerance. 🐛',
        '',
        '...bureau humor. classified. 🚪'
      ],
      '/time': [`Current time: ${new Date().toLocaleString()}`],
      '/status': [
        'Mission Status: ✅ OPERATIONAL',
        'Stack Readiness: 🛰️ DEPLOYMENT READY',
        'Threat Level: 🔒 SECURED',
        'Motivation: 🚀 MAXIMUM THRUST',
        'Uptime: 💡 99.9% SOVEREIGN'
      ],
      '/clear': ['clear'],
      '/cls': ['clear'],
      'clear': ['clear'],
      'cls': ['clear']
    }

    // Check if user forgot the "/" prefix for valid commands
    const validCommands = ['help', 'about', 'skills', 'projects', 'roadmap', 'contact', 'joke', 'time', 'status']
    const commandWithoutSlash = command.replace(/^\//, '') // Remove leading slash if exists
    
    if (!command.startsWith('/') && validCommands.includes(commandWithoutSlash)) {
      return [
        `Did you mean "/${commandWithoutSlash}"? 🤔`,
        `Tip: Commands need a "/" prefix. Try "/${commandWithoutSlash}" instead!`
      ]
    }

    const funnyResponses = [
      "I'm not going to lie, I didn't see this question coming 😂, ask another one",
      "See, I'm not an AI, QuantDoppler just built me for decoration... if you want smarter answers, pay for my OpenAI API key 💸",
      "Error 404: Sense of humor not found. Try /joke instead 🤖",
      "I'm just a humble terminal, not a search engine. Try /help for things I actually know 🤷‍♂️",
      "That's not in my programming manual! Type /help for commands that won't break me 🔧",
      "Did you try turning it off and on again? No? Well, neither did I 🔄",
      "I'm fluent in Python, JavaScript, and sarcasm. Your command speaks none of these 😏",
      "My creator taught me to code, not to read minds. /help might help though 🧠"
    ]

    if (responses[command]) {
      return responses[command]
    } else if (command === '') {
      return []
    } else {
      const randomResponse = funnyResponses[Math.floor(Math.random() * funnyResponses.length)]
      return [randomResponse]
    }
  }

  // Handle command submission
  const handleCommandSubmit = (e) => {
    e.preventDefault()
    if (!currentCommand.trim()) return

    const newLines = [...terminalLines]
    newLines.push(`$ ${currentCommand}`)
    
    const response = processCommand(currentCommand)
    
    // Handle special commands that trigger actions
    if (currentCommand.toLowerCase().trim() === '/roadmap') {
      setTimeout(() => {
        const roadmapSection = document.getElementById('roadmap')
        if (roadmapSection) {
          roadmapSection.scrollIntoView({ behavior: 'smooth' })
        }
      }, 1000) // Delay to show the response first
    }
    
    if (response[0] === 'clear') {
      setTerminalLines([
        "$ whoami", 
        userData?.personal?.name || "QuantDoppler"
      ])
    } else {
      // Add a separator line before the response (except for empty responses)
      if (response.length > 0) {
        newLines.push('--------------------------------------') // Visual separator
      }
      response.forEach(line => newLines.push(line))
      // Add another separator line after the response
      if (response.length > 0) {
        newLines.push('--------------------------------------') // Visual separator
      }
      setTerminalLines(newLines)
    }

    // Add to command history
    if (currentCommand.trim() && !commandHistory.includes(currentCommand)) {
      setCommandHistory(prev => [...prev, currentCommand])
    }
    
    setCurrentCommand('')
    setHistoryIndex(-1)
  }

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (commandHistory.length > 0) {
        const newIndex = historyIndex === -1 ? commandHistory.length - 1 : Math.max(0, historyIndex - 1)
        setHistoryIndex(newIndex)
        setCurrentCommand(commandHistory[newIndex])
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (historyIndex >= 0) {
        const newIndex = historyIndex + 1
        if (newIndex >= commandHistory.length) {
          setHistoryIndex(-1)
          setCurrentCommand('')
        } else {
          setHistoryIndex(newIndex)
          setCurrentCommand(commandHistory[newIndex])
        }
      }
    }
  }

  // Handle terminal resize
  const handleResizeStart = (e) => {
    e.preventDefault()
    setIsResizing(true)
    const clientY = e.type === 'mousedown' ? e.clientY : e.touches[0].clientY
    setResizeStart({
      y: clientY,
      startHeight: terminalHeight
    })
  }

  const handleResizeMove = (e) => {
    if (!isResizing) return
    e.preventDefault()
    
    const clientY = e.type === 'mousemove' ? e.clientY : e.touches[0].clientY
    const deltaY = clientY - resizeStart.y
    const newHeight = resizeStart.startHeight + deltaY
    
    // Constrain height between 200px and 600px
    const constrainedHeight = Math.max(200, Math.min(600, newHeight))
    setTerminalHeight(constrainedHeight)
  }

  const handleResizeEnd = () => {
    setIsResizing(false)
  }

  // Add event listeners for resize
  useEffect(() => {
    if (isResizing) {
      const handleMouseMove = (e) => handleResizeMove(e)
      const handleMouseUp = () => handleResizeEnd()
      const handleTouchMove = (e) => handleResizeMove(e)
      const handleTouchEnd = () => handleResizeEnd()

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.addEventListener('touchmove', handleTouchMove)
      document.addEventListener('touchend', handleTouchEnd)

      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        document.removeEventListener('touchmove', handleTouchMove)
        document.removeEventListener('touchend', handleTouchEnd)
      }
    }
  }, [isResizing, resizeStart, terminalHeight])

  const scrollToProjects = () => {
    const projectSection = document.getElementById('projects')
    if (projectSection) {
      projectSection.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const scrollToExperience = () => {
    const experienceSection = document.getElementById('professional')
    if (experienceSection) {
      experienceSection.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Video */}
      <div className="absolute inset-0 z-0">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover opacity-60"
        >
          <source src="/assets/Hero_background_video.mp4" type="video/mp4" />
        </video>
        {/* Dark overlay for better text contrast */}
        <div className="absolute inset-0 bg-black/40" />
      </div>

      {/* Main Terminal Interface */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6">
        <motion.div 
          className={`grid grid-cols-1 gap-8 items-center transition-all duration-300 ${isTerminalExpanded ? 'lg:grid-cols-1' : 'lg:grid-cols-2'}`}
          layout
        >
          
          {/* Left Side - Terminal Window */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1 }}
            className={`order-2 lg:order-1 ${isTerminalExpanded ? 'w-full max-w-4xl mx-auto' : ''}`}
          >
            <div className={`bg-black/90 backdrop-blur-sm border border-green-500/30 rounded-lg overflow-hidden shadow-2xl transition-all duration-300 ${isTerminalExpanded ? 'shadow-green-500/30 border-green-400/50' : 'shadow-green-500/20'} relative`}>
              {/* Terminal Header */}
              <div className="bg-gradient-to-r from-green-900/50 to-green-800/50 px-4 py-3 border-b border-green-500/30 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="ml-4 text-green-400 font-mono text-sm">
                    zsh - quantdoppler@cosmic-os: ~
                  </span>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-green-400/70 font-mono text-xs">
                    {currentTime.toLocaleTimeString()}
                  </div>
                  <motion.button
                    onClick={() => {
                      if (isTerminalExpanded) {
                        setTerminalHeight(320) // Reset to default height
                        setIsTerminalExpanded(false)
                      } else {
                        setTerminalHeight(500) // Expand to larger height
                        setIsTerminalExpanded(true)
                      }
                    }}
                    className="text-green-400/70 hover:text-green-400 transition-colors p-1 rounded"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    title={isTerminalExpanded ? "Collapse terminal" : "Expand terminal"}
                  >
                    {isTerminalExpanded ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    )}
                  </motion.button>
                </div>
              </div>

              {/* Terminal Content */}
              <div 
                ref={terminalRef}
                style={{ height: `${terminalHeight}px` }}
                className="p-6 overflow-y-auto transition-all duration-200 ease-out"
              >
                <div className="font-mono text-sm space-y-1">
                  <AnimatePresence>
                    {terminalLines.map((line, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className={`
                          ${typeof line === 'string' && line.startsWith('$') ? 'text-green-400' : 'text-white'}
                          ${line === '' ? 'h-4' : ''}
                        `}
                      >
                        {line || '\u00A0'}
                      </motion.div>
                    ))}
                  </AnimatePresence>
                  
                  {/* Interactive Command Input */}
                  {terminalLines.length >= 2 && (
                    <motion.form
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 1 }}
                      onSubmit={handleCommandSubmit}
                      className="flex items-center space-x-2 mt-4"
                    >
                      <span className="text-green-400">$</span>
                      <input
                        ref={inputRef}
                        type="text"
                        value={currentCommand}
                        onChange={(e) => setCurrentCommand(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onFocus={() => setIsInputFocused(true)}
                        onBlur={() => setIsInputFocused(false)}
                        className="flex-1 bg-transparent text-white border-none outline-none"
                        placeholder="Type /help for commands..."
                        autoComplete="off"
                      />
                      <motion.span
                        animate={{ opacity: isInputFocused ? [1, 0, 1] : 1 }}
                        transition={{ repeat: isInputFocused ? Infinity : 0, duration: 1 }}
                        className="text-green-400"
                      >
                        _
                      </motion.span>
                    </motion.form>
                  )}
                  
                  {/* Social Links Buttons */}
                  {terminalLines.length >= 2 && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 1.5 }}
                      className="flex space-x-4 mt-6 pt-4 border-t border-green-500/30"
                    >
                      <motion.a
                        href={userData?.personal?.github || "https://github.com/quantdoppler20"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-2 px-4 py-2 bg-gray-800/50 hover:bg-gray-700/70 border border-gray-600/50 hover:border-green-400/50 rounded-lg transition-all duration-300 group"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <svg className="w-5 h-5 text-gray-400 group-hover:text-green-400 transition-colors" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                        </svg>
                        <span className="text-gray-300 group-hover:text-white transition-colors">GitHub</span>
                      </motion.a>

                    </motion.div>
                  )}
                </div>
              </div>
              
              {/* Resize Handle */}
              <div
                onMouseDown={handleResizeStart}
                onTouchStart={handleResizeStart}
                className={`absolute bottom-0 right-0 w-4 h-4 cursor-se-resize group ${isResizing ? 'bg-green-400/30' : 'hover:bg-green-400/20'} transition-colors duration-200`}
                style={{ clipPath: 'polygon(100% 0%, 0% 100%, 100% 100%)' }}
                title="Drag to resize terminal"
              >
                {/* Resize icon - three diagonal lines */}
                <div className="absolute bottom-1 right-1 opacity-50 group-hover:opacity-80 transition-opacity">
                  <div className="w-0.5 h-2 bg-green-400 rotate-45 absolute bottom-0 right-0"></div>
                  <div className="w-0.5 h-1.5 bg-green-400 rotate-45 absolute bottom-0.5 right-0.5"></div>
                  <div className="w-0.5 h-1 bg-green-400 rotate-45 absolute bottom-1 right-1"></div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Side - HUD Interface */}
          {!isTerminalExpanded && (
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 50 }}
              transition={{ duration: 1, delay: 0.5 }}
              className="order-1 lg:order-2 space-y-6 mt-20 sm:mt-10 lg:mt-0"
            >
            {/* Main Identity Card */}
            <div className="bg-black/80 backdrop-blur-sm border border-cyan-500/30 rounded-lg p-6 relative overflow-hidden mt-16 sm:mt-20 lg:mt-8">
              {/* Animated corner brackets */}
              <div className="absolute top-2 left-2 w-4 h-4 border-l-2 border-t-2 border-cyan-400"></div>
              <div className="absolute top-2 right-2 w-4 h-4 border-r-2 border-t-2 border-cyan-400"></div>
              <div className="absolute bottom-2 left-2 w-4 h-4 border-l-2 border-b-2 border-cyan-400"></div>
              <div className="absolute bottom-2 right-2 w-4 h-4 border-r-2 border-b-2 border-cyan-400"></div>

              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-cyan-400 font-mono text-sm">OPERATOR_ID</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-green-400 font-mono text-xs">ONLINE</span>
                  </div>
                </div>

                {/* QD Logo */}
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  className="flex justify-center mb-6"
                >
                  <div className="relative w-28 h-28 rounded-2xl border-2 border-cyan-400 shadow-lg shadow-cyan-400/40 flex items-center justify-center"
                    style={{ background: '#1a6fb5' }}
                  >
                    <img
                      src="/assets/qd-logo-futuristic.png"
                      alt="QuantDoppler"
                      className="w-full h-full object-cover rounded-2xl"
                      onError={(e) => {
                        e.target.style.display = 'none'
                        e.target.parentElement.style.background = 'linear-gradient(135deg, #1a6fb5 0%, #0f3460 100%)'
                      }}
                    />
                    {/* Status indicator */}
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-400 rounded-full border-2 border-black flex items-center justify-center">
                      <div className="w-2 h-2 bg-black rounded-full animate-pulse"></div>
                    </div>
                  </div>
                </motion.div>

                {/* Hero Headline */}
                <div className="text-center mb-3">
                  <p className="text-cyan-400/70 font-mono text-xs tracking-[0.25em] uppercase mb-1">Hi, We Are</p>
                  <h1 className="text-4xl md:text-5xl font-display font-black leading-tight tracking-wider">
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-green-400">
                      QuantDoppler
                    </span>
                  </h1>
                  <p className="text-white/80 font-semibold text-sm mt-2 leading-snug">
                    Full-Stack Engineering Bureau
                  </p>
                </div>

                <p className="text-xs text-gray-400 mb-4 text-center font-mono tracking-wide leading-relaxed">
                  AI-Driven Architecture · Enterprise Systems · Zero Handoffs
                </p>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-4 mt-6">
                  <motion.div 
                    onClick={scrollToExperience}
                    className="bg-green-500/10 border border-green-500/30 rounded p-3 cursor-pointer transition-all duration-300 hover:bg-green-500/20 hover:border-green-400/50 hover:shadow-lg hover:shadow-green-400/20 group"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="text-green-400 font-mono text-xs group-hover:text-green-300 transition-colors">ENGAGEMENTS</div>
                    <div className="text-white font-bold group-hover:text-green-100 transition-colors">10+ Years</div>
                    <div className="text-gray-400 text-xs group-hover:text-green-100 transition-colors">Enterprise Grade</div>
                    <motion.div
                      className="text-green-400 text-xs mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      initial={{ opacity: 0 }}
                      whileHover={{ opacity: 1 }}
                    >
                      Click to explore →
                    </motion.div>
                  </motion.div>
                  <motion.div 
                    onClick={scrollToProjects}
                    className="bg-cyan-500/10 border border-cyan-500/30 rounded p-3 cursor-pointer transition-all duration-300 hover:bg-cyan-500/20 hover:border-cyan-400/50 hover:shadow-lg hover:shadow-cyan-400/20 group"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="text-cyan-400 font-mono text-xs group-hover:text-cyan-300 transition-colors">PROGRAMS</div>
                    <div className="text-white font-bold group-hover:text-cyan-100 transition-colors">100+ Deployed</div>
                    <motion.div
                      className="text-cyan-400 text-xs mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      initial={{ opacity: 0 }}
                      whileHover={{ opacity: 1 }}
                    >
                      Click to explore →
                    </motion.div>
                  </motion.div>
                </div>
              </div>
            </div>

            {/* Command Interface */}
            <AnimatePresence>
              {showInterface && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                  className="space-y-4"
                >
                  {/* Mission Statement */}
                  <div className="bg-black/80 backdrop-blur-sm border border-yellow-500/30 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                      <span className="text-yellow-400 font-mono text-xs">MISSION_BRIEF</span>
                    </div>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      {userData?.personal?.description || "Building innovative solutions that bridge technology and human experience."}
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={scrollToProjects}
                      className="group flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-600 to-cyan-600 hover:from-green-500 hover:to-cyan-500 rounded-lg border border-cyan-400/30 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-400/30"
                    >
                      <span className="text-white font-medium">EXPLORE_PROJECTS</span>
                      <motion.span
                        className="text-white"
                        animate={{ x: [0, 5, 0] }}
                        transition={{ repeat: Infinity, duration: 2 }}
                      >
                        →
                      </motion.span>
                    </button>
                  </div>

                  {/* Social Links */}
                  <div className="mt-4">
                    <p className="text-cyan-400/50 font-mono text-xs mb-3 tracking-widest">// ORBIT_LINKS</p>
                    <div className="flex flex-wrap gap-3">
                      {[
                        { label: 'GitHub', href: 'https://github.com/quantdoppler20', icon: (
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0 1 12 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/></svg>
                        )},
                        { label: 'LinkedIn', href: 'https://linkedin.com/company/quantdoppler', icon: (
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                        )},
                        { label: 'Twitter', href: 'https://twitter.com/quantdoppler', icon: (
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                        )},
                        { label: 'Discord', href: 'https://discord.gg/quantdoppler', icon: (
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057.1 18.082.114 18.105.134 18.12a19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>
                        )},
                        { label: 'Stack Overflow', href: 'https://stackoverflow.com/users/quantdoppler', icon: (
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M15.725 0l-1.72 1.277 6.39 8.588 1.716-1.277L15.725 0zm-3.94 3.418l-1.369 1.644 8.225 6.85 1.369-1.644-8.225-6.85zm-3.15 4.465l-.905 1.94 9.702 4.517.904-1.94-9.701-4.517zm-1.85 4.86l-.43 2.08 10.373 2.152.43-2.08L6.785 12.743zM6 18h11v2H6v-2zm-.387 2.27l-.43 2.082 10.376 2.149.431-2.08-10.377-2.151z"/></svg>
                        )},
                        { label: 'Dev.to', href: 'https://dev.to/quantdoppler', icon: (
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M7.42 10.05c-.18-.16-.46-.23-.84-.23H6l.02 2.44.04 2.45.56-.02c.41 0 .63-.07.83-.26.24-.24.26-.36.26-2.2 0-1.91-.02-1.96-.29-2.18zM0 4.94v14.12h24V4.94H0zM8.56 15.3c-.44.58-1.06.77-2.53.77H4.71V8.53h1.4c1.67 0 2.16.18 2.6.9.27.43.29.6.32 2.57.05 2.23-.02 2.73-.47 3.3zm5.09-5.47h-2.47v1.77h1.52v1.28l-.72.04-.75.03v1.77l1.22.03 1.2.04v1.28h-1.6c-1.53 0-1.6-.01-1.87-.3l-.3-.28v-3.16c0-3.02.01-3.18.25-3.48.23-.31.25-.31 1.88-.31h1.64v1.29zm4.68 5.45c-.17.43-.64.79-1 .79-.18 0-.45-.15-.67-.39-.32-.32-.45-.63-.82-2.08l-.9-3.39-.45-1.67h.76c.4 0 .75.02.75.05 0 .06 1.16 4.54 1.26 4.83.04.15.32-.7.73-2.3l.66-2.52.74-.04c.4-.02.73 0 .73.04 0 .14-1.67 6.38-1.8 6.68z"/></svg>
                        )},
                      ].map((social, i) => (
                        <motion.a
                          key={social.label}
                          href={social.href}
                          target="_blank"
                          rel="noreferrer"
                          title={social.label}
                          className="relative flex items-center justify-center w-10 h-10 rounded-lg bg-black/60 border border-cyan-400/20 text-cyan-400/70 hover:text-cyan-400 hover:border-cyan-400/60 hover:bg-cyan-400/10 transition-all duration-300"
                          animate={{
                            y: [0, -4, 0],
                            boxShadow: [
                              '0 0 0px rgba(6,182,212,0)',
                              '0 0 12px rgba(6,182,212,0.4)',
                              '0 0 0px rgba(6,182,212,0)',
                            ]
                          }}
                          transition={{
                            duration: 2.4,
                            repeat: Infinity,
                            delay: i * 0.4,
                            ease: 'easeInOut',
                          }}
                          whileHover={{ scale: 1.2 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          {social.icon}
                        </motion.a>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
          )}
        </motion.div>
      </div>

      {/* Floating HUD Elements */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Top System Bar */}
        <div className="absolute top-4 left-4 right-4 flex justify-between items-center">
          <div className="bg-black/80 backdrop-blur-sm border border-cyan-500/30 rounded px-4 py-2">
            <span className="text-cyan-400 font-mono text-xs">SYSTEM_STATUS: OPERATIONAL</span>
          </div>
          <div className="bg-black/80 backdrop-blur-sm border border-green-500/30 rounded px-4 py-2">
            <span className="text-green-400 font-mono text-xs">
              {currentTime.toLocaleDateString()} {currentTime.toLocaleTimeString()}
            </span>
          </div>
        </div>

        {/* Floating Data Points */}
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-cyan-400 rounded-full opacity-60"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              scale: [1, 1.5, 1],
              opacity: [0.6, 1, 0.6],
            }}
            transition={{
              duration: 2 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}

        {/* Scan Lines Effect */}
        <div className="absolute inset-0 opacity-5">
          <div 
            className="w-full h-full"
            style={{
              backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0, 255, 65, 0.1) 2px, rgba(0, 255, 65, 0.1) 4px)',
            }}
          />
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 3 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 pointer-events-none"
      >
        <div className="flex flex-col items-center space-y-2">
          <span className="text-cyan-400 font-mono text-xs">SCROLL_TO_EXPLORE</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="w-6 h-10 border-2 border-cyan-400/50 rounded-full flex justify-center"
          >
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ repeat: Infinity, duration: 2 }}
              className="w-1 h-3 bg-cyan-400 rounded-full mt-2"
            />
          </motion.div>
        </div>
      </motion.div>

    </section>
  )
}

export default HeroSection
